/**
 * Auto-Save Service
 * Speichert alle Änderungen sofort in localStorage
 * Verhindert Datenverlust bei Stromausfällen
 *
 * Phase 0: Schreibt über StorageEngine (KV) — siehe docs/adr/ADR-0002.
 */

import { getStorageEngine } from '../app/di/container';
import { KV_COLLECTION } from '../infrastructure/storage/StorageEngine';
import compressedLocalStorage, { shouldCompressKvKey } from '../utils/localStorageCompression.js';
import { isBusinessMergedPayloadKey } from '../utils/sharedCore';
import { LEGACY_STANDALONE_DOMAIN_KEYS } from '../utils/localStorageHealth';

const LEGACY_BLOCKED_KV_KEYS = new Set(LEGACY_STANDALONE_DOMAIN_KEYS);

function assertAutoSaveKeyAllowed(key) {
  const k = String(key);
  if (isBusinessMergedPayloadKey(k) || LEGACY_BLOCKED_KV_KEYS.has(k)) {
    const err = new Error(
      `[AUTO-SAVE] Direct write blocked for "${k}". Use queueBusinessPayloadWrite / field writes.`
    );
    console.warn(err.message);
    throw err;
  }
}

class AutoSaveService {
  constructor() {
    this.pendingSaves = new Map();
    this.saveQueue = [];
    this.isProcessing = false;
    this.saveInterval = null;
    this.debounceDelay = 50;
    this._initialized = false;
  }

  /**
   * Initialisiere Auto-Save
   */
  initialize() {
    if (this._initialized) {
      return;
    }
    this._initialized = true;

    window.addEventListener('pagehide', () => {
      void this.flushAllAndPersist();
    });

    window.addEventListener('beforeunload', () => {
      void this.flushAllAndPersist();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        void this.flushAllAndPersist();
      }
    });

    this.saveInterval = setInterval(() => {
      this.processQueue();
    }, 5000);
  }

  /**
   * Speichere Daten sofort (mit Debounce)
   */
  async save(key, data, options = {}) {
    const {
      immediate = false,
      priority = 'normal' // 'high', 'normal', 'low'
    } = options;

    // Für kritische Daten: sofort speichern
    if (immediate || priority === 'high') {
      return this.saveImmediate(key, data);
    }

    // Für normale Daten: Debounce
    const existing = this.pendingSaves.get(key);
    if (existing) {
      clearTimeout(existing.timeout);
    }

    const timeout = setTimeout(() => {
      void this.saveImmediate(key, data);
      this.pendingSaves.delete(key);
    }, this.debounceDelay);

    this.pendingSaves.set(key, {
      data,
      timeout,
      priority,
      timestamp: Date.now()
    });
    
    // Gib immer ein Ergebnis zurück, auch wenn debounced
    return { success: true, key, queued: true };
  }

  /**
   * Speichere sofort (ohne Debounce)
   */
  async saveImmediate(key, data) {
    try {
      assertAutoSaveKeyAllowed(key);

      if (shouldCompressKvKey(key)) {
        const result = compressedLocalStorage.setItem(key, data);
        if (!result || !result.success) {
          throw new Error(result?.error || 'Compressed save failed');
        }
        const engine = getStorageEngine();
        if (engine._backend?.flushPendingWrites) {
          await engine._backend.flushPendingWrites();
        }
        const readBack = compressedLocalStorage.getItem(key);
        if (readBack == null) {
          console.warn(`[AUTO-SAVE] Compressed read-back empty for ${key}, data may still be queued`);
        }
        return { success: true, key, compressed: result.compressed };
      }

      const engine = getStorageEngine();
      const dataString = JSON.stringify(data);
      engine.putRaw(KV_COLLECTION, key, dataString);

      if (engine._backend?.flushPendingWrites) {
        await engine._backend.flushPendingWrites();
      }

      const verify = engine.getRaw(KV_COLLECTION, key);
      if (verify !== dataString) {
        console.warn(`[AUTO-SAVE] Verify mismatch for ${key}, accepting write (IDB async path)`);
      }

      return { success: true, key };
    } catch (error) {
      console.error(`❌ [AUTO-SAVE] Failed to save ${key}:`, error);
      
      // Retry bei Quota-Fehler
      if (error.name === 'QuotaExceededError') {
        this.handleQuotaExceeded(key, data);
      }
      
      return { success: false, error: error.message };
    }
  }

  /**
   * Speichere mehrere Keys gleichzeitig (atomar)
   */
  saveBatch(operations) {
    try {
      operations.forEach(({ key }) => assertAutoSaveKeyAllowed(key));

      const engine = getStorageEngine();
      const backup = {};

      operations.forEach(({ key }) => {
        const existing = engine.getRaw(KV_COLLECTION, key);
        if (existing) {
          backup[key] = existing;
        }
      });

      operations.forEach(({ key, data }) => {
        if (shouldCompressKvKey(key)) {
          const r = compressedLocalStorage.setItem(key, data);
          if (!r || !r.success) {
            throw new Error(r?.error || 'Compressed batch save failed');
          }
        } else {
          engine.putRaw(KV_COLLECTION, key, JSON.stringify(data));
        }
      });

      const allVerified = operations.every(({ key, data }) => {
        if (shouldCompressKvKey(key)) {
          const readBack = compressedLocalStorage.getItem(key);
          return readBack != null && JSON.stringify(readBack) === JSON.stringify(data);
        }
        const saved = engine.getRaw(KV_COLLECTION, key);
        return saved === JSON.stringify(data);
      });

      if (!allVerified) {
        Object.entries(backup).forEach(([key, value]) => {
          engine.putRaw(KV_COLLECTION, key, value);
        });
        throw new Error('Batch save verification failed');
      }

      return { success: true };
    } catch (error) {
      console.error('❌ [AUTO-SAVE] Batch save failed:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Verarbeite Save-Queue
   */
  async processQueue() {
    if (this.isProcessing || this.pendingSaves.size === 0) {
      return;
    }

    this.isProcessing = true;

    try {
      // Sortiere nach Priorität
      const sorted = Array.from(this.pendingSaves.entries())
        .sort((a, b) => {
          const priorityOrder = { high: 0, normal: 1, low: 2 };
          return priorityOrder[a[1].priority] - priorityOrder[b[1].priority];
        });

      for (const [key, { data }] of sorted) {
        await this.saveImmediate(key, data);
        this.pendingSaves.delete(key);
      }
    } catch (error) {
      console.error('❌ [AUTO-SAVE] Error processing queue:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Speichere alle pending Saves sofort
   */
  async flushAll() {
    await this.processQueue();

    for (const [key, { data }] of this.pendingSaves.entries()) {
      await this.saveImmediate(key, data);
    }

    this.pendingSaves.clear();
  }

  /** Pending Auto-Saves + IndexedDB-Flush (Stromausfall). */
  async flushAllAndPersist() {
    await this.flushAll();
    try {
      const { flushPendingStorageWrites } = await import(
        '../infrastructure/storage/businessPayloadWrite'
      );
      await flushPendingStorageWrites();
    } catch (error) {
      console.warn('[AUTO-SAVE] Storage flush failed:', error?.message || error);
    }
  }

  /**
   * Behandle Quota-Exceeded-Fehler
   */
  handleQuotaExceeded(key, data) {
    console.warn('⚠️ [AUTO-SAVE] Quota exceeded, attempting cleanup');

    try {
      const engine = getStorageEngine();
      const backupKeys = engine.listKvKeys().filter((k) => k.includes('_backup_'));

      backupKeys.slice(0, 10).forEach((k) => {
        engine.delete(KV_COLLECTION, k);
      });

      return this.saveImmediate(key, data);
    } catch (error) {
      console.error('❌ [AUTO-SAVE] Cleanup failed:', error);
    }
  }

  /**
   * Stoppe Auto-Save
   */
  stop() {
    if (this.saveInterval) {
      clearInterval(this.saveInterval);
      this.saveInterval = null;
    }
    this.flushAll();
  }
}

export const autoSaveService = new AutoSaveService();
export default autoSaveService;





